import type { ReplayEvent } from "../packages/db/src/public-types";
import { SLOT, slotId, slotStart } from "./slots";

export interface SlotResponse { slot: string; plays_from: number; replay: boolean; quiet?: boolean; note?: string; events: ReplayEvent[] }

export interface ReplayHandlers {
  /** `animate` is false for dots that were already due before the page loaded. */
  onEvent(ev: ReplayEvent, animate: boolean): void;
  onSlot(res: SlotResponse): void;
}

const SEED_SLOTS = 4; // the last hour appears as settled dots on first load

/**
 * Plays the server's 15-minute batches on a delay. Each dot fires at plays_from + t, so every viewer sees the
 * same trickle at the same moment. It fetches the next slot as the current one ends.
 */
export class Replay {
  private timers: number[] = [];

  constructor(private readonly h: ReplayHandlers) {}

  async start(): Promise<void> {
    // Always name the slot: a bare /api/events could be served stale from a cache and would then
    // fast-forward through every slot since.
    const first = await this.fetchSlot(slotId(Math.floor(Date.now() / 1000) - SLOT));
    if (!first) return;
    this.h.onSlot(first); // before seeding, so the handlers know whether this is live or replay
    const start = slotStart(first.slot);
    const seeds = await Promise.all(
      Array.from({ length: SEED_SLOTS }, (_, i) => this.fetchSlot(slotId(start - (SEED_SLOTS - i) * SLOT))),
    );
    for (const s of seeds) s?.events.forEach((ev) => this.h.onEvent(ev, false));
    this.play(first, false);
  }

  private async fetchSlot(slot: string): Promise<SlotResponse | null> {
    const res = await fetch(`/api/events?slot=${slot}`);
    return res.ok ? ((await res.json()) as SlotResponse) : null;
  }

  private play(res: SlotResponse, announce = true): void {
    if (announce) this.h.onSlot(res);
    const now = Date.now() / 1000;
    for (const ev of [...res.events].sort((a, b) => a.t - b.t)) {
      const due = res.plays_from + ev.t;
      if (due <= now) this.h.onEvent(ev, false);
      else this.timers.push(window.setTimeout(() => this.h.onEvent(ev, !document.hidden), (due - now) * 1000));
    }
    const next = slotId(slotStart(res.slot) + SLOT);
    const wait = Math.max(0, (res.plays_from + SLOT - now) * 1000);
    this.timers.push(window.setTimeout(() => void this.advance(next), wait));
  }

  private async advance(slot: string, attempt = 0): Promise<void> {
    const res = await this.fetchSlot(slot).catch(() => null);
    if (res) return this.play(res);
    // The slot may not be closed yet, or the network blipped: back off gently.
    this.timers.push(window.setTimeout(() => void this.advance(slot, attempt + 1), Math.min(60_000, 5_000 * (attempt + 1))));
  }
}
